/**
 * Task-level alerting (owner ask 2026-08-25): tasks previously had only a
 * deadline — habits got reminders, tasks got silence. This section lets a
 * task carry Reminder Contracts: bound, so completing the task resolves
 * every reminder instantly through the canonical completion bridge.
 */
import { useCallback, useEffect, useState } from 'react'
import { Bell, X } from 'lucide-react'
import { Timestamp } from '../ui/Timestamp'
import {
  createReminder,
  listActiveReminders,
  reminderAction,
  type ReminderContract,
} from '../../services/reminderService'
import { BRAND } from '../../config/brand'

const INTERVALS = [
  { v: 5, label: 'every 5 min' },
  { v: 10, label: 'every 10 min' },
  { v: 15, label: 'every 15 min' },
  { v: 30, label: 'every 30 min' },
  { v: 60, label: 'hourly' },
  { v: 240, label: 'every 4 hours' },
  { v: 1440, label: 'daily' },
]

export function TaskReminderSection({ taskId, taskTitle, deadline, embedded = false, hardDeadline = false }: {
  taskId: number
  taskTitle: string
  deadline?: string | null
  /** A hard complete-by: nagging past it is noise, so "For" defaults to the due date. */
  hardDeadline?: boolean
  /** Inside the detail rail: a divider instead of its own card. */
  embedded?: boolean
}) {
  const [mine, setMine] = useState<ReminderContract[]>([])
  const [adding, setAdding] = useState(false)
  const [interval, setIntervalMin] = useState(30)
  // Owner 2026-09-05: from when, for how long, and whether quiet hours pause it.
  const [startAt, setStartAt] = useState('')
  const [forHow, setForHow] = useState<'until_done' | '1d' | '3d' | '1w' | 'due'>(hardDeadline && deadline ? 'due' : 'until_done')
  const [quiet, setQuiet] = useState<'respect' | 'sovereign'>('respect')
  const [critical, setCritical] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const all = await listActiveReminders()
    setMine(all.filter(
      (c) => c.bound_item_type === 'task' && c.bound_item_id === taskId,
    ))
  }, [taskId])

  useEffect(() => { void load() }, [load])

  const add = async () => {
    setBusy(true)
    try {
      const days: Record<string, number> = { '1d': 1, '3d': 3, '1w': 7 }
      const expires = forHow === 'until_done' ? undefined
        : forHow === 'due' ? (deadline ? new Date(deadline.length <= 10 ? `${deadline}T23:59:00` : deadline).toISOString() : undefined)
        : new Date(Date.now() + days[forHow] * 86_400_000).toISOString()
      await createReminder({
        obligation_text: taskTitle,
        interval_minutes: interval,
        bound_item_type: 'task',
        bound_item_id: taskId,
        critical,
        quiet_hours_mode: quiet,
        ...(startAt ? { first_fire_at: new Date(startAt).toISOString() } : {}),
        ...(expires ? { expires_at: expires } : {}),
        ...(deadline ? { deadline_at: deadline } : {}),
      })
      setAdding(false)
      setCritical(false)
      await load()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={embedded ? 'pt-4 border-t border-soft' : 'rounded-2xl border border-soft bg-surface p-4'} data-testid="task-reminders">
      {/* The same heading, icon and verb style as the habit page's rail
          (owner 2026-09-06: one rail order on every item page — Fill the gaps ·
          Plan · Hold me to it · keeping up). */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-app-meta font-bold uppercase tracking-widest text-secondary">
          <Bell className="h-3.5 w-3.5 text-brand" /> Hold me to it
        </h3>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className="text-app-meta font-medium text-brand hover:underline" data-testid="task-reminder-add">
            Add a reminder
          </button>
        )}
      </div>

      {mine.length === 0 && !adding && (
        <p className="mt-2 text-xs text-muted">
          Nothing set to remind you about this yet. Add one and {BRAND.assistant} will keep at it until it is done; finishing the task stops it.
        </p>
      )}

      {adding && (
        <div className="mt-3 space-y-2" data-testid="reminder-form">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="text-app-micro uppercase tracking-wide text-muted">
              From
              <input
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                className="mt-1 block w-full min-h-10 rounded-xl border border-soft bg-surface px-2 text-sm normal-case tracking-normal text-primary"
                aria-label="Start reminding from"
              />
              <span className="block normal-case tracking-normal text-muted">{startAt ? '' : 'Now, if left empty'}</span>
            </label>
            <label className="text-app-micro uppercase tracking-wide text-muted">
              Every
              <select
                value={interval}
                onChange={(e) => setIntervalMin(Number(e.target.value))}
                aria-label="Reminder cadence"
                className="mt-1 block w-full min-h-10 rounded-xl border border-soft bg-surface px-2 text-sm normal-case tracking-normal text-primary"
              >
                {INTERVALS.map((i) => (
                  <option key={i.v} value={i.v}>{i.label.replace(/^every /, '')}</option>
                ))}
              </select>
            </label>
            <label className="text-app-micro uppercase tracking-wide text-muted">
              For
              <select
                value={forHow}
                onChange={(e) => setForHow(e.target.value as typeof forHow)}
                aria-label="Keep reminding for"
                className="mt-1 block w-full min-h-10 rounded-xl border border-soft bg-surface px-2 text-sm normal-case tracking-normal text-primary"
              >
                <option value="until_done">Until it is done</option>
                <option value="1d">One day</option>
                <option value="3d">Three days</option>
                <option value="1w">One week</option>
                {deadline && <option value="due">Until the due date</option>}
              </select>
            </label>
            <label className="text-app-micro uppercase tracking-wide text-muted">
              Quiet hours
              <select
                value={quiet}
                onChange={(e) => setQuiet(e.target.value as typeof quiet)}
                aria-label="During quiet hours"
                className="mt-1 block w-full min-h-10 rounded-xl border border-soft bg-surface px-2 text-sm normal-case tracking-normal text-primary"
              >
                <option value="respect">Pause, resume after</option>
                <option value="sovereign">Keep going through them</option>
              </select>
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex min-h-10 cursor-pointer items-center gap-1.5 rounded-xl border border-soft px-3 text-xs text-secondary hover:border-bad/50">
              <input
                type="checkbox"
                checked={critical}
                onChange={(e) => setCritical(e.target.checked)}
                className="accent-bad"
              />
              critical
            </label>
            <span className="text-app-micro text-muted">Delivered by the channels in your reminder settings.</span>
            <button
              type="button"
              disabled={busy}
              onClick={() => void add()}
              className="ml-auto min-h-10 rounded-xl bg-warn px-4 text-xs font-bold text-white hover:opacity-90 disabled:opacity-50"
              data-testid="reminder-start"
            >
              Start holding me
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              aria-label="Cancel"
              className="min-h-10 rounded-lg px-2 text-muted hover:text-secondary"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {mine.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {mine.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-xl bg-surface-alt px-3 py-2">
              <span className="text-xs text-primary">
                every {c.interval_minutes >= 60
                  ? `${Math.round(c.interval_minutes / 60)}h`
                  : `${c.interval_minutes}m`}
                {c.critical && (
                  <span className="ml-2 rounded-full bg-bad/15 px-1.5 py-0.5 text-app-micro font-bold uppercase text-bad">critical</span>
                )}
                {c.quiet_hours_mode === 'sovereign' && (
                  <span className="ml-2 text-muted">through quiet hours</span>
                )}
                {c.op_state === 'snoozed' && c.next_fire_at && (
                  <span className="ml-2 text-muted">snoozed until <Timestamp value={c.next_fire_at} mode="datetime" /></span>
                )}
                {c.op_state !== 'snoozed' && c.next_fire_at && (
                  <span className="ml-2 text-muted">next <Timestamp value={c.next_fire_at} mode="datetime" /></span>
                )}
              </span>
              <button
                type="button"
                onClick={async () => { await reminderAction(c.id, 'stop'); await load() }}
                className="ml-auto min-h-8 rounded-lg px-2 text-app-micro text-muted hover:text-bad hover:bg-bad/10"
              >
                Stop
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
